import { useCallback, useMemo } from 'react'
import { useLatest } from 'react-use'
import { isEqual } from 'lodash-es'
import WallEdit from '@editor/components/WallEdit'
import { WALL_EDIT_IN_PROGRESS_BLOCK } from '@editor/components/WallEdit/constants'
import type { FloorplanClickListener } from '@editor/hooks/useEmitFloorplanClickListener'
import type { MarqueeSelectionListener } from '@editor/hooks/useEmitMarqueeSelectionListener'
import { useCameraStore } from '@modugen/ui-toolkit'
import { useControlStore, useEditElementStore } from '@editorStores'
import {
  useBlockAction,
  useElementTypes,
  useFloorplanClickListener,
  useHighlightPrimary,
  useIfcMode,
  useMarqueeSelectionListener,
  useModelClickListeners,
  useSceneElement,
  useTypeInteraction,
} from '@editorHooks'
import { eventMatchesPointerModifier } from 'src/shortcuts/pointerModifiers'
import useIFCImportWallEditAutoSave from './useIFCImportWallEditAutoSave'

/** Planar-model element types that can be selected for editing during IFC import. */
const PLANAR_SELECTABLE_TYPES: ElementTypes[] = [
  'outer_walls',
  'inner_walls',
  'slabs',
  'roof_slabs',
]

/**
 * Enables store-driven selection of planar-model elements (walls, slabs, roof slabs) while the
 * planar-model selection mode is active on the IFC import page. Clicking such an element sets it as
 * the active element (highlighted in the scene); the IFC planar edit drawer then renders the
 * matching edit form. Selection is cleared when leaving the page.
 */
const useIFCPlanarSelection = (): void => {
  const selectionMode = useControlStore(state => state.selectionMode)
  const isPlanarMode = selectionMode === 'planar-model-selection'

  // A key-based selection mode (e.g. picking reference roof slabs for "Zuschnitt") owns clicks while
  // active — don't let the planar selection change the active element (which would remount the edit
  // form and drop that mode's own selection).
  const selectionModeKey = useControlStore(state => state.selectionModeKey)
  const isKeyedSelectionActive = selectionModeKey !== null

  const planarSelectableTypes = useControlStore(state => state.planarSelectableTypes)

  // While a drawing tool is active, canvas clicks belong to that tool (the wall drawer, the roof
  // form, or the single-wall opening editor) — don't let planar selection pick up (and open the
  // edit form / drag handles for) elements.
  const { ifcMode } = useIfcMode()
  const isDrawModeActive =
    ifcMode === 'draw-walls' || ifcMode === 'draw-roofs' || ifcMode === 'draw-openings'

  const activeElement = useEditElementStore(state => state.activeElement)
  const setActiveElement = useEditElementStore(state => state.setActiveElement)
  const multiActiveElements = useEditElementStore(state => state.multiActiveElements)
  const setMultiActiveElements = useEditElementStore(state => state.setMultiActiveElements)
  const setActiveOpening = useEditElementStore(state => state.setActiveOpening)
  const setMultiActiveOpenings = useEditElementStore(state => state.setMultiActiveOpenings)

  const isOrthographic = useCameraStore(state => state.isOrthographic)

  // `WallEdit` registers this block while a move/extend is in progress. A click→click extend ends
  // with a stationary click on whatever wall the endpoint landed on; that click resolves before the
  // `mouseup` that ends the edit, so reading the block live via `isBlockedFn()` at click time keeps
  // it from selecting the other wall — matching Structural Planning, which refuses selection while
  // its wall editor is active.
  const { isBlockedFn } = useBlockAction(WALL_EDIT_IN_PROGRESS_BLOCK, 'subscriber')

  // Persist a move/resize to the backend as soon as the drag ends (auto-save on draw).
  const saveWallEdit = useIFCImportWallEditAutoSave()

  const elementTypes = useElementTypes()

  // A step may restrict selection to a subset of planar types; otherwise all are selectable.
  const selectableTypes = useMemo(
    () => planarSelectableTypes ?? PLANAR_SELECTABLE_TYPES,
    [planarSelectableTypes],
  )

  // Make the planar element types clickable/hoverable while planar selection is active.
  useTypeInteraction(selectableTypes, isPlanarMode)

  // The wall-edit case is handled by the block above (checked live inside `selectPlanarElement`), so
  // the listeners stay registered throughout the edit rather than being toggled off — toggling
  // re-armed the listener exactly at edit-stop and let the finalizing click through.
  const selectionEnabled = isPlanarMode && !isKeyedSelectionActive && !isDrawModeActive

  // The single/multi selections are read through refs so the click/marquee callbacks keep a stable
  // identity and the listeners don't re-register after every selection change.
  const activeElementLatest = useLatest(activeElement)
  const multiActiveElementsLatest = useLatest(multiActiveElements)
  const elementTypesLatest = useLatest(elementTypes)
  const selectableTypesLatest = useLatest(selectableTypes)

  const isSelectableWall = useCallback(
    (guid: string) => {
      const type = elementTypesLatest.current[guid]
      return (
        (type === 'inner_walls' || type === 'outer_walls') &&
        selectableTypesLatest.current.includes(type)
      )
    },
    [elementTypesLatest, selectableTypesLatest],
  )

  const selectPlanarElement = useCallback(
    (guid: string, isMultiSelect = false) => {
      // Refuse selection while a wall move/extend is in progress (incl. the click that ends it).
      if (isBlockedFn()) return
      const type = elementTypesLatest.current[guid]
      if (!type || !selectableTypesLatest.current.includes(type)) return

      // Selecting a wall/slab deselects any active/selected opening so the drawer shows the
      // element form (element and opening selections are mutually exclusive).
      setActiveOpening(null)
      setMultiActiveOpenings([])

      // The multi-select modifier toggles the wall in the wall-only multi selection (any single active wall is
      // folded into it); non-wall types always select single.
      if (isMultiSelect && isSelectableWall(guid)) {
        const active = activeElementLatest.current
        const base = [
          ...(active && active !== guid && isSelectableWall(active) ? [active] : []),
          ...multiActiveElementsLatest.current,
        ]
        setMultiActiveElements(base.includes(guid) ? base.filter(g => g !== guid) : [...base, guid])
        setActiveElement(null)
        return
      }

      setMultiActiveElements([])
      setActiveElement(guid)
    },
    [
      isBlockedFn,
      elementTypesLatest,
      selectableTypesLatest,
      isSelectableWall,
      activeElementLatest,
      multiActiveElementsLatest,
      setActiveElement,
      setMultiActiveElements,
      setActiveOpening,
      setMultiActiveOpenings,
    ],
  )

  // 3D model clicks (enabled while the multi-select modifier is held so those clicks reach
  // the multi selection).
  useModelClickListeners(
    event =>
      selectPlanarElement(
        event.object.name,
        eventMatchesPointerModifier(event.nativeEvent, 'multiSelect'),
      ),
    [selectPlanarElement],
    selectionEnabled,
    false,
    true,
  )

  // 2D floorplan clicks (walls/slabs in the orthographic view emit through a separate channel).
  const onFloorplanClick = useCallback<FloorplanClickListener>(
    (guid, event) =>
      selectPlanarElement(guid, eventMatchesPointerModifier(event?.nativeEvent, 'multiSelect')),
    [selectPlanarElement],
  )
  useFloorplanClickListener(onFloorplanClick, { enabled: selectionEnabled })

  // Marquee selection fills the wall-only multi selection (edited via the importer's wall
  // multi-edit); non-wall planar types and IFC/GLB objects in the marquee are ignored.
  const onMarqueeSelect = useCallback<MarqueeSelectionListener>(
    ids => {
      const wallGuids = ids.filter(isSelectableWall)
      if (isEqual(wallGuids, multiActiveElementsLatest.current)) return
      setMultiActiveElements(wallGuids)
      if (wallGuids.length > 0) {
        setActiveElement(null)
        setActiveOpening(null)
        setMultiActiveOpenings([])
      }
    },
    [
      isSelectableWall,
      multiActiveElementsLatest,
      setActiveElement,
      setMultiActiveElements,
      setActiveOpening,
      setMultiActiveOpenings,
    ],
  )
  useMarqueeSelectionListener(onMarqueeSelect, { enabled: selectionEnabled })

  // Render the interactive 2D wall editor (move/extend handles) for the selected wall, reusing
  // Structural Planning's WallEdit. Only in the orthographic (2D) view, where it operates.
  const activeElementType = activeElement ? elementTypes[activeElement] : null
  const isWallActive = activeElementType === 'outer_walls' || activeElementType === 'inner_walls'
  const wallEditProps = useMemo(
    () => ({
      wallGuid: activeElement ?? '',
      // Editing state is tracked inside WallEdit (it owns the wall-edit block); here we only need to
      // persist the change once the edit stops.
      onEditStart: () => undefined,
      onEditStop: () => {
        if (activeElement) saveWallEdit(activeElement)
      },
      reset: () => setActiveElement(null),
    }),
    [activeElement, setActiveElement, saveWallEdit],
  )
  useSceneElement({
    enabled: isPlanarMode && isOrthographic && isWallActive && !isDrawModeActive,
    properties: wallEditProps,
    component: WallEdit,
  })

  const highlightedIds = useMemo(() => {
    if (!isPlanarMode) return []
    if (multiActiveElements.length > 0) return multiActiveElements
    return activeElement ? [activeElement] : []
  }, [isPlanarMode, activeElement, multiActiveElements])
  useHighlightPrimary(highlightedIds, isPlanarMode)
}

export default useIFCPlanarSelection
